/* eslint-disable no-underscore-dangle */
import { MeasureItem, MeasureMenu, LabelMenu, LabelItem } from './styles';

import { getXYPositon } from 'services/utils/elementPosition';

import Icon from '@map/components/Icons';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
/**
 * Importação da biblioteca leaflet a qual utiliza o OpenStreetMap,
 * é uma biblioteca de mapeamento leve e de codigo aberto, utiliza um banco
 * de dados geografico editável gratuito.
 */
import L from 'leaflet';
/**
 * useState é um hook que nos permite rastrear o estado em um
 * componente de função, o estado geralmente se refere a dados ou
 * propriedads que precisam ser rastreador em um aplicativo.
 * 
 * useRef permite que persista valores entre renderizações, ele pode
 * ser usado para armazenar um valor mutável que não causa uma nova
 * renderização quando atualizado e pode ser usado para acessar di-
 * retamente um elemento do DOM.
 * 
 * useEffect é um hook que permite executar efeitos colaterais em
 * seus componentes, por exemplo: buscar dados, atualizar diretamente
 * o DOM e cronômetros, ele aceita dois argumentos o segundo é opcional.
 */
import { useState, useRef, useEffect } from 'react';
/**
 * ReactDOM é um pacote que fornece métodos específicos do DOM que podem
 * ser usados no nível superior do seu aplicativo e como uma saída de 
 * emergencia para sair do modelo React.
 */
import ReactDOM from 'react-dom';

/**
 * ReactDOM.render() função recebe dois argumentos, codigo HTML e um 
 * elemento html, a função tem como objetivo exibir o codigo HTML espe-
 * cificado dentro do elemento HTML especificado.
 */
const onClose = () => {
  ReactDOM.render(null, document.getElementById('apendix'));
};

/**
 * Função que dispara um menu ao clicar em cima de uma
 * feature leaflet com botão direito do mouse, para isso
 * é criado um novo componente na DOM que é desfeito assim
 * que concluído seu trabalho
 *
 * @param {object} selectedLayer {object} A camada leaflet que estamos interagindo com
 * @param {object} anchorPosition {object} Coordenadas do clique do mouse { mouseX, mouseY }
 * @param {array} features {array} A lista de features que estamos interagindo, importante para poder retornar a nova lista de features para atualização de state ao excluir uma feature, já que o state do leaflet é diferente
 * @param {object} map {objetct} Leaflet map
 * @param {function} listener {function} Função de callback com alguns escutadores como ctxmenu:featureremoved
 * @param {object} measurementsOptions {object} Opções para a exibição das medidas, ao ocultar e reexibir
 * @param {boolean} options Opções do menu
 */
export const FeatureContextMenu = ({
  selectedLayer,
  anchorPosition,
  features,
  map,
  listener,
  measurementsOptions,
  options,
}) => {
  const isArea =
    selectedLayer &&
    (selectedLayer instanceof L.Polygon || selectedLayer instanceof L.Circle);
  const { mouseX, mouseY } = anchorPosition;
  const menuRef = useRef(null);

  options = {
    chooseUnit: true,
    overlapLayer: true,
    areaPerimeterMeasurements: false,
    edit: true,
    removeFeature: true,
    enframe: true,
    label: true,
    ...options,
  };

  const {
    chooseUnit,
    overlapLayer,
    areaPerimeterMeasurements,
    edit,
    removeFeature,
    enframe,
    label,
  } = options;

  const [showAreaStatus, setShowAreaStatus] = useState(
    (isArea &&
      areaPerimeterMeasurements &&
      selectedLayer?._measurementOptions.showArea) ||
      false
  );
  const [showPerimeterStatus, setShowPerimeterStatus] = useState(
    (isArea &&
      areaPerimeterMeasurements &&
      selectedLayer?._measurementOptions.showTotalDistance) ||
      false
  );

  useEffect(() => {
    if (mouseY !== null && mouseX !== null) {
      let position = getXYPositon(
        { x: mouseX, y: mouseY, height: 0, width: 0 },
        menuRef.current
      );
      menuRef.current.style.left = position.left + 'px';
      menuRef.current.style.top = position.top + 'px';
    }
  }, [mouseX, mouseY]);

  // Apêndice de menu de medidas
  const getMeasureItens = () => {
    let measureItens = [
      { param: 'm', distanceLabel: 'm', areaLabel: 'm²' },
      { param: 'km', distanceLabel: 'km', areaLabel: 'km²' },
      { param: 'ha', areaLabel: 'ha' },
    ];
    return measureItens.map((measureObject, i) => {
      return (isArea && measureObject.areaLabel) ||
        (!isArea && measureObject.distanceLabel) ? (
        <MeasureItem
          key={i}
          onClick={() => {
            selectedLayer && isArea
              ? selectedLayer.setAreaUnit(measureObject.param)
              : selectedLayer.setDistanceUnit(measureObject.param);
            listener('ctxmenu:closed');
            onClose();
          }}
        >
          {selectedLayer &&
          isArea &&
          selectedLayer._areaUnit === measureObject.param ? (
            <Icon i="map-contextmenu-check" />
          ) : selectedLayer &&
            !isArea &&
            selectedLayer._distanceUnit === measureObject.param ? (
            <Icon i="map-contextmenu-check" />
          ) : null}
          {isArea ? measureObject.areaLabel : measureObject.distanceLabel}
        </MeasureItem>
      ) : null;
    });
  };

  // Função que deve ser ativada ao clicar para excluir no menu de contexto
  const removeFeatureFunction = (featureToDelete) => {
    let filteredFeatures = features.filter(
      (f) => f._leaflet_id.toString() !== featureToDelete._leaflet_id.toString()
    );
    listener('ctxmenu:featureremoved', {
      oldFeatures: features,
      newFeatures: filteredFeatures,
      featureDeleted: featureToDelete,
    });
    listener('ctxmenu:closed');
    onClose();
    featureToDelete.remove();
  };

  return (
    <Popper
      open={mouseY !== null}
      role={undefined}
      transition
      disablePortal
      ref={menuRef}
      style={
        mouseY !== null && mouseX !== null
          ? {
              zIndex: 1500,
            }
          : undefined
      }
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === 'bottom' ? 'center top' : 'center bottom',
          }}
        >
          <Paper>
            <ClickAwayListener onClickAway={onClose}>
              <MenuList
                autoFocusItem={anchorPosition.mouseY !== null}
                id="menu-list-grow"
              >
                {edit && !(selectedLayer instanceof L.Marker) && (
                  <MenuItem
                    onClick={() => {
                      if (selectedLayer && selectedLayer.pm.enabled()) {
                        selectedLayer.pm.disable();
                      } else if (selectedLayer && !selectedLayer.pm.enabled()) {
                        selectedLayer.pm.enable({
                          allowSelfIntersection: false,
                        });
                      }
                      listener('ctxmenu:closed');
                      onClose();
                    }}
                  >
                    {selectedLayer && selectedLayer.pm.enabled()
                      ? 'Finalizar edição'
                      : 'Editar'}
                  </MenuItem>
                )}

                {removeFeature && (
                  <MenuItem
                    onClick={() => removeFeatureFunction(selectedLayer)}
                  >
                    Excluir
                  </MenuItem>
                )}

                {enframe && (
                  <MenuItem
                    onClick={() => {
                      !(selectedLayer instanceof L.Marker)
                        ? map.flyToBounds(selectedLayer.getBounds())
                        : map.flyTo(selectedLayer.getLatLng(), 13);
                      listener('ctxmenu:closed');
                      onClose();
                    }}
                  >
                    Enquadrar
                  </MenuItem>
                )}

                {selectedLayer?.showMeasurements && label && (
                  <MenuItem
                    onClick={() => {
                      if (selectedLayer && !selectedLayer._measurementLayer) {
                        selectedLayer.showMeasurements &&
                          selectedLayer.showMeasurements(measurementsOptions);
                      } else if (selectedLayer?._measurementLayer) {
                        selectedLayer.hideMeasurements &&
                          selectedLayer.hideMeasurements();
                      }
                      listener('ctxmenu:closed');
                      onClose();
                    }}
                  >
                    {selectedLayer._measurementLayer
                      ? 'Ocultar rótulo'
                      : 'Exibir rótulo'}
                  </MenuItem>
                )}

                {overlapLayer && [
                  <MenuItem
                    key="bringToFront"
                    onClick={() => {
                      selectedLayer.bringToFront();
                      listener('ctxmenu:closed');
                      onClose();
                    }}
                    disabled={selectedLayer instanceof L.Marker}
                  >
                    Enviar para Frente
                  </MenuItem>,
                  <MenuItem
                    key="bringToBack"
                    onClick={() => {
                      selectedLayer.bringToBack();
                      listener('ctxmenu:closed');
                      onClose();
                    }}
                    disabled={selectedLayer instanceof L.Marker}
                  >
                    Enviar para trás
                  </MenuItem>,
                ]}

                {areaPerimeterMeasurements && (
                  <LabelMenu
                    onMouseOver={(e) => {
                      let originElement = e.target;
                      let targetElement = originElement.getElementsByClassName(
                        'subMenu'
                      )[0];
                      if (originElement && targetElement) {
                        let position = getXYPositon(
                          originElement,
                          targetElement,
                          { alignLeftOnMaxWidth: true }
                        );
                        targetElement.style.left = position.left + 'px';
                        targetElement.style.top = position.top + 'px';
                      }
                    }}
                    disabled={selectedLayer instanceof L.Marker}
                  >
                    Rótulo
                    <Icon i="map-contextmenu-arrow" />
                    <Paper className="subMenu">
                      <MenuList>
                        <LabelItem
                          key="area"
                          onClick={() => {
                            selectedLayer.setMeasureOptions({
                              ...selectedLayer._measurementOptions,
                              showArea: !showAreaStatus,
                            });
                            setShowAreaStatus(!showAreaStatus);
                            listener('ctxmenu:closed');
                            onClose();
                          }}
                        >
                          {showAreaStatus && (
                            <Icon i="map-contextmenu-bluecheck" />
                          )}{' '}
                          Área
                        </LabelItem>
                        <LabelItem
                          key="perimetro"
                          onClick={() => {
                            selectedLayer.setMeasureOptions({
                              ...selectedLayer._measurementOptions,
                              showTotalDistance: !showPerimeterStatus,
                              vertexDistances: {
                                ...selectedLayer._measurementOptions
                                  .vertexDistances,
                                enable: !showPerimeterStatus,
                              },
                            });
                            setShowPerimeterStatus(!showPerimeterStatus);
                            listener('ctxmenu:closed');
                            onClose();
                          }}
                        >
                          {showPerimeterStatus && (
                            <Icon i="map-contextmenu-bluecheck" />
                          )}{' '}
                          Distância entre Vértices
                        </LabelItem>
                      </MenuList>
                    </Paper>
                  </LabelMenu>
                )}

                {chooseUnit && (
                  <MeasureMenu
                    onMouseOver={(e) => {
                      let originElement = e.target;
                      let targetElement = originElement.getElementsByClassName(
                        'subMenu'
                      )[0];
                      if (originElement && targetElement) {
                        let position = getXYPositon(
                          originElement,
                          targetElement,
                          { alignLeftOnMaxWidth: true }
                        );
                        targetElement.style.left = position.left + 'px';
                        targetElement.style.top = position.top + 'px';
                      }
                    }}
                    disabled={selectedLayer instanceof L.Marker}
                  >
                    Alterar unidade de medida
                    <Icon i="map-contextmenu-arrow" />
                    <Paper className="subMenu">
                      <MenuList>{getMeasureItens()}</MenuList>
                    </Paper>
                  </MeasureMenu>
                )}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

FeatureContextMenu.properties = {
  ignore: true,
};

export default FeatureContextMenu;
